import React, { useCallback, useState } from 'react';
import { FileError } from 'react-dropzone';
import { AlertIcon, Box, Inscription } from '@common-fe/common-fe';

import AppButton from '@/components/controls/AppButton';
import { SuccessModal } from '@/components/elements';
import UploadFileErrorPopup from '@/components/elements/DropScan/UploadFileErrorPopup';
import { ModalWrapper } from '@/components/wrappers';

import { useCombineHSAModal } from './hooks/useCombineHSAModal';
import { CombineHSASectionType } from './types/combineHSA.types';
import { CombineHSAConfirmStep } from './CombineHSAConfirmStep';
import { CombineHSASection } from './CombineHSASection';

interface Props {
  accountNumber: string;
  employeeId: string;
  firstName?: string;
  lastName?: string;
  visible: boolean;
  onSetVisible: (value: boolean) => void;
}

export const CombineHSAModal: React.FC<Props> = ({ accountNumber, employeeId, firstName, lastName, visible, onSetVisible }) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [fileError, setFileError] = useState<FileError | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successFirstName, setSuccessFirstName] = useState('');

  const handleFileErrorClose = useCallback(() => setFileError(null), []);

  const {
    ssn,
    selectedFromId,
    selectedToId,
    selectedFromAccount,
    selectedToAccount,
    isSubmitAttempted,
    handleClose,
    handleCancel,
    handleNext,
    handleSelectFrom,
    handleSelectTo,
    handleClearSelection,
  } = useCombineHSAModal({ employeeId, onSetVisible });

  const handleModalClose = useCallback((value: boolean) => {
    if (!value) setStep(1);
    handleClose(value);
  }, [handleClose]);

  const handleClickNext = useCallback(() => {
    handleNext();
    if (selectedFromId && selectedToId) setStep(2);
  }, [handleNext, selectedFromId, selectedToId]);

  const handleBack = useCallback(() => setStep(1), []);

  const handleSuccess = useCallback(() => {
    setSuccessFirstName(selectedFromAccount!.personFirstName);
    handleModalClose(false);
    setShowSuccess(true);
  }, [selectedFromAccount, handleModalClose]);

  const handleSuccessClose = useCallback(() => {
    setShowSuccess(false);
    onSetVisible(false);
  }, [onSetVisible]);

  return (
    <>
    <ModalWrapper
      visible={visible}
      onSetVisible={handleModalClose}
      testId="CombineHSAModal"
      title="Combine HSA"
      maxWidth="720px"
      isClosingByClickOutsideBlocked
      description={(
        <Box margin={{ top: 'spacing8' }}>
          <Inscription color="textBody">
            Transfer the full balance and close the source account.
          </Inscription>
        </Box>
      )}
      contentStyle={{ cursor: 'default', backgroundColor: 'white' }}
    >
      {step === 2 && selectedFromAccount && selectedToAccount ? (
        <CombineHSAConfirmStep
          selectedFromAccount={selectedFromAccount}
          selectedToAccount={selectedToAccount}
          employeeId={employeeId}
          onBack={handleBack}
          onSuccess={handleSuccess}
          onFileError={setFileError}
        />
      ) : (
        <Box direction="column" gap="spacing20" width="100%">
          <Box
            direction="column"
            gap="spacing20"
            pad="spacing20"
            background="module"
            round="moduleRound"
          >
            <CombineHSASection
              sectionType={CombineHSASectionType.FROM}
              currentAccountNumber={accountNumber}
              employeeId={employeeId}
              ssn={ssn}
              firstName={firstName}
              lastName={lastName}
              selectedFromId={selectedFromId}
              selectedToId={selectedToId}
              otherSectionAccount={selectedToAccount}
              hasError={isSubmitAttempted && !selectedFromId && !!selectedToId}
              onSelect={handleSelectFrom}
              onClearSelection={handleClearSelection}
            />
            <Box align="center" justify="center">
              <Inscription size="2xl">
                ↓
              </Inscription>
            </Box>
            <CombineHSASection
              sectionType={CombineHSASectionType.TO}
              currentAccountNumber={accountNumber}
              employeeId={employeeId}
              ssn={ssn}
              firstName={firstName}
              lastName={lastName}
              selectedFromId={selectedFromId}
              selectedToId={selectedToId}
              otherSectionAccount={selectedFromAccount}
              hasError={isSubmitAttempted && !!selectedFromId && !selectedToId}
              onSelect={handleSelectTo}
            />
          </Box>
          <Box direction="column" gap="spacing20" margin={{ top: 'spacing4' }}>
            {isSubmitAttempted && !selectedFromId && !selectedToId && (
              <Box data-testid="combine-hsa-modal_no-accounts-error" direction="row" align="center" justify="end" gap="spacing4">
                <Inscription color="danger">No accounts selected</Inscription>
                <AlertIcon size="20px" />
              </Box>
            )}
            <Box direction="row" justify="end" gap="spacing12">
              <AppButton data-testid="combine-hsa-modal_cancel-button" buttonType="secondary" width="160px" onClick={handleCancel}>
                Cancel
              </AppButton>
              <AppButton data-testid="combine-hsa-modal_next-button" width="160px" onClick={handleClickNext}>
                Next (1/2)
              </AppButton>
            </Box>
          </Box>
        </Box>
      )}
    </ModalWrapper>
    {fileError && (
      <UploadFileErrorPopup fileError={fileError} onClose={handleFileErrorClose} />
    )}
    <SuccessModal
      testId="combine-hsa_success"
      visible={showSuccess}
      header={`HSAs for ${successFirstName} have been combined`}
      buttonText="Got It!"
      onSetVisible={handleSuccessClose}
    />
    </>
  );
};
